<template>
    <CompassPanel
        :modelValue="isVisible"
        :title="localizes.title"
        size="large"
        class="objective-update-panel"
        :primaryAction="primaryAction"
        :cancelButtonProps="cancelButtonProps"
        @closed="closePanel"
    >
        <div class="objective-update-panel__heading">
            <h2 class="objective-update-panel__heading-title typographies-heading-h2">
                {{ $l10n.__('Définissez votre objectif') }}
            </h2>
        </div>

        <div class="objective-update-panel__content">
            <CompassFormItem
                :label="localizes.form.name"
                size="small"
                orientation="horizontal"
                class="objective-update-panel__content-name"
            >
                <CompassInput
                    v-model="updatedObjective.name"
                    :placeholder="localizes.form.placeholder.name"
                    size="small"
                />
            </CompassFormItem>

            <CompassDescriptionList
                class="objective-update-panel__content-category"
                type="alternative"
            >
                <CompassDescriptionListItem :label="localizes.form.category">
                    {{ objective.category.label }}
                </CompassDescriptionListItem>
            </CompassDescriptionList>

            <CompassDescriptionList
                class="objective-update-panel__content-main-informations"
                type="alternative"
            >
                <CompassDescriptionListItem :label="localizes.form.assignee">
                    <CompassUser
                        size="small"
                        :firstname="assigneeFormated.firstname"
                        :lastname="assigneeFormated.lastname"
                        :showCaption="false"
                    >
                        <CompassAvatar
                            size="small"
                            :colorType="assigneeFormated.colorType"
                            :initials="assigneeFormated.initials"
                            :type="assigneeFormated.type"
                        />
                    </CompassUser>
                </CompassDescriptionListItem>

                <CompassDescriptionListItem v-if="itemFormated" :label="localizes.form.item">
                    {{ itemFormated }}
                </CompassDescriptionListItem>

                <CompassDescriptionListItem
                    :label="localizes.form.duration"
                    class="objective-update-panel__content-main-informations-period"
                >
                    <CompassIcon icon="calendar-fill" />
                    {{ durationFormated }}
                </CompassDescriptionListItem>

                <CompassDescriptionListItem
                    :label="localizes.form.period"
                    class="objective-update-panel__content-main-informations-period"
                >
                    <CompassIcon icon="calendar-fill" />
                    {{ periodFormated }}
                </CompassDescriptionListItem>
            </CompassDescriptionList>

            <CompassFormItem
                v-if="!hasRefinements"
                :label="localizes.form.result"
                size="small"
                orientation="horizontal"
                class="objective-update-panel__content-result-editable"
            >
                <AmountInputFormated
                    v-model="updatedObjective.turnover.amount"
                    :placeholder="localizes.form.placeholder.result"
                    :currencySymbol="objectiveCurrencySymbol"
                    size="small"
                />

                <div
                    v-if="hasPreviousTurnover && !isComputeLoading"
                    class="objective-previous-datas"
                >
                    <span
                        class="objective-previous-datas__amount typographies-paragraph-small-semibold"
                    >
                        {{ previousTurnoverAmountFormated }}
                    </span>
                    <span
                        class="objective-previous-datas__period typographies-paragraph-small-regular"
                    >
                        {{ previousTurnoverYear }}
                    </span>
                </div>

                <CompassSkeleton v-else-if="isComputeLoading" class="objective-previous-datas" />
            </CompassFormItem>

            <template v-else>
                <div class="objective-update-panel__content-result">
                    <span
                        class="objective-update-panel__content-result-label typographies-paragraph-medium-regular"
                    >
                        {{ $l10n.__('Résultat attendu ajusté') }}
                    </span>
                    <span
                        class="objective-update-panel__content-result-amount typographies-paragraph-x-large-semibold"
                    >
                        {{ turnoverAmountAdjustedFormated }}
                    </span>

                    <div
                        v-if="hasPreviousTurnover && !isComputeLoading"
                        class="objective-update-panel__content-result-previous"
                    >
                        <span
                            class="objective-previous-datas__amount typographies-paragraph-small-semibold"
                        >
                            {{ previousTurnoverAmountFormated }}
                        </span>
                        <span
                            class="objective-previous-datas__period typographies-paragraph-small-regular"
                        >
                            {{ previousTurnoverYear }}
                        </span>
                    </div>

                    <CompassSkeleton
                        v-else-if="isComputeLoading"
                        class="objective-update-panel__content-result-previous"
                    />
                </div>

                <div class="objective-update-panel__content-refinements">
                    <div
                        v-for="refinement in updatedObjective.refinements"
                        :key="refinement.period.date_start"
                        class="objective-update-panel__content-refinements-item"
                    >
                        <CompassFormItem :label="getRefinementLabel(refinement)" size="small">
                            <AmountInputFormated
                                v-model="refinement.turnover.amount"
                                :currencySymbol="objectiveCurrencySymbol"
                                :placeholder="localizes.form.placeholder.refinement"
                                :disabled="!refinement.isEditable"
                                size="small"
                            />
                        </CompassFormItem>

                        <div
                            v-if="
                                hasPreviousRefinements &&
                                getPreviousRefinement(refinement) &&
                                !isComputeLoading
                            "
                            class="objective-previous-datas"
                        >
                            <span
                                class="objective-previous-datas__amount typographies-paragraph-small-semibold"
                            >
                                {{ getPreviousRefinement(refinement).amount }}
                            </span>
                            <span
                                class="objective-previous-datas__period typographies-paragraph-small-regular"
                            >
                                {{ getPreviousRefinement(refinement).period }}
                            </span>
                        </div>

                        <CompassSkeleton
                            v-else-if="isComputeLoading"
                            class="objective-previous-datas"
                        />
                    </div>
                </div>
            </template>
        </div>
    </CompassPanel>
</template>

<script>
import AvatarMixins from 'mixins/AvatarMixins';
import MonthsConfig from 'Config/authenticated/Months';
import objectiveService from 'api/objectives/objectiveService';
import AmountInputFormated from 'vcomponents/inputs/AmountInputFormated.vue';

export default {
    components: { AmountInputFormated },
    mixins: [AvatarMixins],
    props: {
        objective: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isSaveButtonLoading: false,
            isComputeLoading: false,
            updatedObjective: null,
            previousRefinements: [],
            previousTurnover: null,
            localizes: {
                title: this.$l10n.__("Modifier l'objectif"),
                form: {
                    name: this.$l10n.__("Nom de l'objectif"),
                    category: this.$l10n.__("Type d'objectif"),
                    assignee: this.$l10n.__('Responsable'),
                    item: this.$l10n.__('Produit/Service'),
                    duration: this.$l10n.__('Durée'),
                    period: this.$l10n.__('Période'),
                    result: this.$l10n.__('Résultat attendu'),
                    placeholder: {
                        name: this.$l10n.__('Nom de l’objectif'),
                        result: this.$l10n.__('Saisissez un résultat attendu'),
                        refinement: this.$l10n.__('Saisissez une mensualité'),
                    },
                },
                buttons: {
                    save: this.$l10n.__('Enregistrer'),
                    cancel: this.$l10n.__('Annuler'),
                },
            },
            periodDateFormat: 'YYYY-MM-DD',
            isVisible: false,
        };
    },
    computed: {
        assigneeFormated() {
            let assigneeData = null;
            const assignee = this.objective.assignee;

            if (assignee?.id && assignee?.name?.length > 0) {
                const name = assignee.name.split(' ');
                assigneeData = {
                    id: assignee.id,
                    type: assignee.type === 'staff' ? 'default' : 'team',
                    firstname: name[0],
                    lastname: name.slice(1).join(' '),
                    colorType: this.avatarColor(assignee.name),
                    initials: name[0].slice(0, 1).toUpperCase(),
                };
            } else if (assignee.type === 'corp') {
                assigneeData = {
                    id: -1,
                    type: 'company',
                    firstname: this.$l10n.__('Entreprise'),
                    lastname: '',
                    colorType: this.avatarColor(assignee.type),
                    initials: '',
                };
            }

            return assigneeData;
        },
        periodFormated() {
            const start = this.$filters.dateFormatter(
                moment(this.objective.period.date_start).unix(),
                'SLASHED_DATE',
                false,
            );
            const end = this.$filters.dateFormatter(
                moment(this.objective.period.date_end).unix(),
                'SLASHED_DATE',
                false,
            );

            return `${start} - ${end}`;
        },
        durationFormated() {
            switch (this.objective.period.duration) {
                case 'year':
                    return this.$l10n.__('Une année');
                case 'quarter':
                    return this.$l10n.__('Un trimestre');
                case 'month':
                    return this.$l10n.__('Un mois');
                default:
                    return '';
            }
        },
        itemFormated() {
            let itemName = null;
            if (this.objective.item) {
                itemName = this.objective.item.name;
            } else if (this.objective.item_category) {
                itemName = this.objective.item_category.name;
            }

            return itemName;
        },
        turnoverAmountAdjusted() {
            return this.updatedObjective && this.hasRefinements
                ? this.updatedObjective.refinements.reduce(
                      (total, refinement) =>
                          parseFloat((total + Number(refinement.turnover.amount)).toFixed(2)),
                      0,
                  )
                : 0;
        },
        turnoverAmountAdjustedFormated() {
            return this.$filters.numberFormatCurrencyCustomNumberFormat(
                this.turnoverAmountAdjusted,
                {
                    currency: this.objectiveCurrency,
                    currencysymbol: this.objectiveCurrencySymbol,
                },
            );
        },
        previousTurnoverAmount() {
            return this.previousTurnover?.turnover.amount ?? 0;
        },
        previousTurnoverAmountFormated() {
            return this.formatAmount(this.previousTurnover?.turnover);
        },
        previousTurnoverYear() {
            return this.previousTurnover
                ? moment(this.previousTurnover.period.date_start).year()
                : '';
        },
        hasPreviousTurnover() {
            return (
                this.previousTurnover &&
                this.previousTurnoverAmount !== 0 &&
                this.previousTurnoverYear !== ''
            );
        },
        hasRefinements() {
            return this.updatedObjective.refinements.length > 0;
        },
        hasPreviousRefinements() {
            return this.previousRefinements.length > 0;
        },
        objectiveCurrency() {
            return this.objective.turnover.expected.currency;
        },
        objectiveCurrencySymbol() {
            return window.Currency.getCurrencySymbol(this.objectiveCurrency);
        },
        primaryAction() {
            return {
                label: this.localizes.buttons.save,
                loading: this.isSaveButtonLoading,
                action: this.updateObjective,
            };
        },
        cancelButtonProps() {
            return {
                label: this.localizes.buttons.cancel,
            };
        },
    },
    watch: {
        objective() {
            this.initPanel();
        },
        turnoverAmountAdjusted() {
            this.updatedObjective.turnover.amount = this.turnoverAmountAdjusted;
        },
    },
    beforeMount() {
        this.initPanel();
        this.getCompute();
    },
    mounted() {
        this.isVisible = true;
    },
    methods: {
        initPanel() {
            const currentDate = moment().format();
            const refinements = [...this.objective.refinements];
            const refinementsFormated = refinements.map((refinement) => {
                return {
                    period: { ...refinement.period },
                    turnover: { ...refinement.turnover.expected },
                    isEditable: moment(refinement.period.date_end).isAfter(currentDate),
                };
            });

            this.updatedObjective = {
                name: this.objective.name,
                turnover: { ...this.objective.turnover.expected },
                refinements: refinementsFormated,
            };
        },
        updateObjective() {
            this.isSaveButtonLoading = true;
            const formData = {
                ...this.updatedObjective,
                turnover: {
                    ...this.updatedObjective.turnover,
                    amount: this.updatedObjective.turnover.amount.toString(),
                },
                refinements: this.updatedObjective.refinements.map((refinement) => ({
                    period: refinement.period,
                    turnover: {
                        ...refinement.turnover,
                        amount: refinement.turnover.amount.toString(),
                    },
                })),
            };

            objectiveService
                .updateObjective(this.objective.id, formData)
                .then(() => {
                    this.isVisible = false;
                    this.$nextTick(() => {
                        this.$emit('objectiveUpdated');
                    });
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$l10n.__(
                            "Une erreur est survenue lors de la modification de l'objectif, merci de réessayer",
                        ),
                    });
                })
                .finally(() => {
                    this.isSaveButtonLoading = false;
                });
        },
        getCompute() {
            this.isComputeLoading = true;
            const previousPeriod = this.getPreviousPeriod(this.objective.period);

            objectiveService
                .getCompute(
                    this.objective.category.code,
                    this.objectiveCurrency,
                    previousPeriod,
                    this.objective.item?.id ?? null,
                    this.objective.item_category?.id ?? null,
                    { type: this.objective.assignee.type, id: this.objective.assignee.id },
                )
                .then((response) => {
                    this.previousTurnover = response.data.find(
                        (previousTurnover) =>
                            previousTurnover.period.date_start === previousPeriod.date_start &&
                            previousTurnover.period.date_end === previousPeriod.date_end,
                    );
                    this.previousRefinements = response.data.filter(
                        (previousRefinement) =>
                            previousRefinement.period.date_start !== previousPeriod.date_start ||
                            previousRefinement.period.date_end !== previousPeriod.date_end,
                    );
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$l10n.__(
                            "Une erreur est survenue lors de la récupération de l'objectif pour la période précédente, merci de réessayer",
                        ),
                    });
                })
                .finally(() => {
                    this.isComputeLoading = false;
                });
        },
        closePanel() {
            this.$emit('close');
        },
        getRefinementMonthConfig(refinement) {
            const refinementMonth = moment(refinement.period.date_start).month() + 1;
            const refinementMonthFormated =
                refinementMonth < 10 ? `0${refinementMonth}` : refinementMonth;

            return MonthsConfig.find(
                (monthConfig) => monthConfig.month === String(refinementMonthFormated),
            );
        },
        getRefinementLabel(refinement) {
            return this.getRefinementMonthConfig(refinement)?.label ?? '';
        },
        getPreviousRefinement(refinement) {
            const previousPeriod = this.getPreviousPeriod(refinement.period);
            const previousRefinement = this.previousRefinements.find(
                (previousRefinement) =>
                    previousRefinement.period.date_start === previousPeriod.date_start &&
                    previousRefinement.period.date_end === previousPeriod.date_end,
            );

            return previousRefinement
                ? {
                      amount: this.formatAmount(previousRefinement.turnover),
                      period: `${
                          this.getRefinementMonthConfig(previousRefinement)?.labelMinified
                      } ${moment(previousRefinement.period.date_start).year()}`,
                  }
                : null;
        },
        getPreviousPeriod(period) {
            return {
                ...period,
                date_start: moment(period.date_start)
                    .subtract(1, 'year')
                    .format(this.periodDateFormat),
                date_end: moment(period.date_end).subtract(1, 'year').format(this.periodDateFormat),
            };
        },
        formatAmount(amountToFormat) {
            return this.$filters.numberFormatCurrencyCustomNumberFormat(amountToFormat.amount, {
                currency: amountToFormat.currency,
                currencysymbol: window.Currency.getCurrencySymbol(amountToFormat.currency),
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.objective-update-panel {
    &__heading {
        margin-bottom: var(--spacing-3);

        &-title {
            margin: 0;
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);

        &-category {
            :deep() {
                .compass-description-list__item--listing {
                    gap: var(--spacing-3);
                }
            }
        }

        &-main-informations {
            padding: var(--spacing-3) 0;
            border-top: 1px solid var(--colors-common-border-default);
            border-bottom: 1px solid var(--colors-common-border-default);

            &-period {
                :deep() {
                    .compass-description-list__item__content {
                        display: flex;
                        align-items: center;
                        flex-wrap: wrap;
                        gap: var(--spacing-2);
                    }
                }
            }

            :deep() {
                .compass-description-list__item--listing {
                    gap: var(--spacing-3);
                }
            }
        }

        &-result-editable {
            :deep() {
                &.compass-form-item--horizontal {
                    align-items: flex-start;

                    .compass-form-item__input {
                        gap: var(--spacing-1);
                    }
                }
            }
        }

        &-result {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--spacing-1);
            padding: var(--spacing-3);
            border-radius: var(--spacing-0);
            background-color: var(--colors-common-background-app);

            &-label {
                color: var(--colors-common-text-standard-default);
            }

            &-amount {
                color: var(--colors-common-text-standard-active);
                flex-shrink: 0;
                margin-left: auto;
            }

            &-previous {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: var(--spacing-1);
                flex-shrink: 0;
                padding: var(--spacing-0) var(--spacing-2);
                color: var(--colors-common-text-standard-caption);
                border-radius: var(--spacing-0);
                border: 1px solid var(--colors-common-border-default);
                background-color: var(--colors-common-background-subtle);
                min-width: 130px;
            }
        }

        &-refinements {
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            flex-wrap: wrap;
            gap: var(--spacing-1);

            &-item {
                display: flex;
                flex-direction: column;
                gap: var(--spacing-0);
                width: calc((100% / 3) - ((2 / 3) * var(--spacing-1)));
            }
        }
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: var(--spacing-3);
        padding: var(--spacing-3) var(--spacing-5);
        background-color: var(--colors-common-background-default);
        border-top: 1px solid var(--colors-common-border-default);
    }

    .objective-previous-datas {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-1);
        padding: var(--spacing-0) var(--spacing-2);
        color: var(--colors-common-text-standard-caption);
        border-radius: var(--spacing-0);
        background-color: var(--colors-common-background-subtle);

        &__amount {
            flex-shrink: 0;
        }

        &__period {
            text-align: right;
        }
    }
}
</style>
